/** Public contract for durable Preview/Final email delivery. No BCC or message body here. */
export type ShareEmailSendMode = 'check' | 'when_ready' | 'force';
export type ShareEmailDeliveryState =
  | 'waiting' | 'needs_confirmation' | 'sending' | 'sent'
  | 'failed' | 'uncertain' | 'canceled' | 'expired';
export interface ShareEmailFileReadiness {
  id: number;
  fileName: string;
  transferStatus: string;
  previewStatus: string | null;
  ready: boolean;
  detail: string;
}
export interface ShareEmailReadiness {
  shareId: number;
  ready: boolean;
  available: boolean;
  reason?: string;
  /** Fingerprint of the file set/content, not processing progress. */
  version: string;
  files: ShareEmailFileReadiness[];
}
export type ShareEmailDeliveryAlert = 'waiting' | 'files_changed' | 'sent' | 'failed' | 'uncertain' | 'expired' | null;
export interface ShareEmailDelivery {
  id: string;
  state: ShareEmailDeliveryState;
  projectId: string;
  projectName: string;
  projectThumbnailUrl: string | null;
  previewShareId: number;
  previewTitle: string;
  previewUrl: string;
  subject: string;
  createdAt: string;
  expiresAt: string;
  sentAt: string | null;
  /** Confirmed sent history is durable; null while reconciliation is pending. */
  recordedAt: string | null;
  alert: ShareEmailDeliveryAlert;
  /** Stable across polling; changes on each new warning, so sound plays once. */
  alertKey: string | null;
  error: string | null;
  readiness: ShareEmailReadiness;
}
export type ShareEmailDeliveryAction = 'force' | 'wait' | 'cancel' | 'confirm' | 'acknowledge';
export interface ShareEmailDeliveryActionInput {
  action: ShareEmailDeliveryAction;
  version?: string;
}
export interface ShareEmailQueuedResponse {
  queued: true;
  delivery: ShareEmailDelivery;
}

/**
 * Match the intended URL, not a token substring. A monotonic HTML scanner
 * leaves data URIs, comments and malformed quoted tags opaque. Only anchor
 * hrefs and ordinary text URLs count (not images, scripts or hidden attributes).
 */
export function emailBodyContainsShareUrl(html: string, shareUrl: string): boolean {
  if (typeof html !== 'string' || typeof shareUrl !== 'string') return false;
  const decode = (value: string) => value.replace(/&(?:amp|quot|apos|lt|gt|#\d{1,7}|#x[0-9a-f]{1,6});/gi, entity => {
    const names: Record<string, string> = { amp: '&', quot: '"', apos: "'", lt: '<', gt: '>' };
    const key = entity.slice(1, -1).toLowerCase();
    if (names[key]) return names[key];
    const n = key.startsWith('#x') ? parseInt(key.slice(2), 16) : parseInt(key.slice(1), 10);
    return n > 0 && n <= 0x10ffff ? String.fromCodePoint(n) : entity;
  });
  const parse = (value: string): URL | null => {
    try {
      const url = new URL(value);
      return ['https:', 'http:'].includes(url.protocol) && !url.username && !url.password ? url : null;
    } catch { return null; }
  };
  const intended = parse(shareUrl);
  if (!intended) return false;
  const matches = (value: string) => {
    const url = parse(decode(value).trim());
    return !!url && url.origin === intended.origin && url.pathname === intended.pathname &&
      url.search === intended.search && url.hash === intended.hash;
  };
  const textMatches = (text: string) => {
    // Delimited candidates; never retry a suffix of a long attribute/document.
    for (const token of decode(text).split(/[\s<>"']+/)) {
      if (token.startsWith('https://') || token.startsWith('http://')) {
        if (matches(token) || matches(token.replace(/[),.;!?]+$/, ''))) return true;
      }
    }
    return false;
  };
  let cursor = 0;
  let opaque: string | null = null;
  while (cursor < html.length) {
    if (html[cursor] !== '<') {
      const start = cursor;
      while (cursor < html.length && html[cursor] !== '<') cursor++;
      if (!opaque && textMatches(html.slice(start, cursor))) return true;
      continue;
    }
    if (html.startsWith('<!--', cursor)) {
      const end = html.indexOf('-->', cursor + 4);
      if (end < 0) return false;
      cursor = end + 3;
      continue;
    }
    const start = cursor++;
    let quote: string | null = null;
    while (cursor < html.length) {
      const ch = html[cursor++];
      if (quote) { if (ch === quote) quote = null; }
      else if (ch === '"' || ch === "'") quote = ch;
      else if (ch === '>') break;
    }
    if (quote || html[cursor - 1] !== '>') return false;
    const tag = html.slice(start + 1, cursor - 1);
    const name = /^(\s*\/?\s*)([a-z][a-z0-9:-]*)/i.exec(tag);
    if (!name) continue;
    const closing = name[1].includes('/');
    const tagName = name[2].toLowerCase();
    if (opaque) {
      if (closing && tagName === opaque) opaque = null;
      continue;
    }
    if (!closing && ['script', 'style', 'template'].includes(tagName)) { opaque = tagName; continue; }
    if (closing || tagName !== 'a') continue;
    let pos = name[0].length;
    let href: string | undefined;
    while (pos < tag.length) {
      while (pos < tag.length && /\s/.test(tag[pos])) pos++;
      const keyStart = pos;
      while (pos < tag.length && !/[\s=\/]/.test(tag[pos])) pos++;
      const key = tag.slice(keyStart, pos).toLowerCase();
      while (pos < tag.length && /\s/.test(tag[pos])) pos++;
      if (tag[pos] !== '=') { if (pos === keyStart) pos++; continue; }
      pos++;
      while (pos < tag.length && /\s/.test(tag[pos])) pos++;
      const delimiter = tag[pos] === '"' || tag[pos] === "'" ? tag[pos++] : null;
      const valueStart = pos;
      while (pos < tag.length && (delimiter ? tag[pos] !== delimiter : !/\s/.test(tag[pos]))) pos++;
      if (key === 'href' && href === undefined) href = tag.slice(valueStart, pos);
      if (delimiter && pos < tag.length) pos++;
    }
    if (href !== undefined && matches(href)) return true;
  }
  return false;
}